import { useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { Euro } from 'lucide-react';
import { fetchAllRows } from '@/lib/supabaseHelpers';

interface ABSpendSplitProps {
  totalSpend?: number;
  loading?: boolean;
  dateFrom?: Date;
  dateTo?: Date;
}

const ABSpendSplit = ({ totalSpend, loading: externalLoading, dateFrom, dateTo }: ABSpendSplitProps) => {
  const [variantCounts, setVariantCounts] = useState<{ A: number; B: number }>({ A: 0, B: 0 });
  const [variantCompletions, setVariantCompletions] = useState<{ A: number; B: number }>({ A: 0, B: 0 });
  const [spend, setSpend] = useState<number | null>(totalSpend ?? null);
  const [loadingSpend, setLoadingSpend] = useState(totalSpend === undefined);
  const [testStartedAt, setTestStartedAt] = useState<string | null>(null);
  const startedAtRef = useRef<string | null>(null);

  const fetchActiveTestStart = async () => {
    const { data } = await supabase
      .from('ab_test_log')
      .select('started_at')
      .eq('status', 'active')
      .order('started_at', { ascending: false })
      .limit(1)
      .single();
    const startedAt = data?.started_at || null;
    startedAtRef.current = startedAt;
    setTestStartedAt(startedAt);
    return startedAt;
  };

  const fetchVariantData = async (startedAt?: string | null) => {
    const sa = startedAt ?? startedAtRef.current;
    const dateFilters = sa
      ? [{ column: 'created_at', operator: 'gte', value: sa }]
      : [];
    const [pvData, sessData] = await Promise.all([
      fetchAllRows<{ ab_variant: string | null }>('quiz_pageviews', 'ab_variant', [
        { column: 'utm_source', operator: 'not.is', value: null },
        { column: 'ab_variant', operator: 'not.is', value: null },
        ...dateFilters,
      ]),
      fetchAllRows<{ ab_variant: string | null; is_completed: boolean | null }>('quiz_sessions', 'ab_variant, is_completed', [
        { column: 'utm_source', operator: 'not.is', value: null },
        { column: 'ab_variant', operator: 'not.is', value: null },
        ...dateFilters,
      ]),
    ]);
    const counts = { A: 0, B: 0 };
    pvData.forEach(p => { if (p.ab_variant === 'A') counts.A++; else if (p.ab_variant === 'B') counts.B++; });
    setVariantCounts(counts);
    const comp = { A: 0, B: 0 };
    sessData.forEach(s => { if (s.is_completed && s.ab_variant === 'A') comp.A++; else if (s.is_completed && s.ab_variant === 'B') comp.B++; });
    setVariantCompletions(comp);
  };

  const fetchSpend = async (startedAt?: string | null) => {
    if (totalSpend !== undefined) { setSpend(totalSpend); setLoadingSpend(false); return; }
    setLoadingSpend(true);
    const sa = startedAt ?? startedAtRef.current;
    try {
      const body: Record<string, string> = {};
      const since = sa ? sa.slice(0, 10) : (dateFrom ? format(dateFrom, 'yyyy-MM-dd') : null);
      const until = dateTo ? format(dateTo, 'yyyy-MM-dd') : new Date().toISOString().slice(0, 10);
      if (since) {
        body.time_range = JSON.stringify({ since, until });
      } else {
        body.date_preset = 'today';
      }
      const { data } = await supabase.functions.invoke('fetch-meta-ads', { body });
      const quizSpend = data?.summary?.total_spend ?? 0;
      setSpend(quizSpend);
    } catch { setSpend(0); }
    finally { setLoadingSpend(false); }
  };

  useEffect(() => {
    fetchActiveTestStart().then(startedAt => {
      fetchVariantData(startedAt);
      fetchSpend(startedAt);
    });
    const channel = supabase
      .channel('ab-spend-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'quiz_pageviews' }, () => fetchVariantData())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'quiz_sessions' }, () => fetchVariantData())
      .subscribe();
    const interval = setInterval(() => fetchSpend(), 60_000);
    return () => { supabase.removeChannel(channel); clearInterval(interval); };
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (totalSpend !== undefined) setSpend(totalSpend);
  }, [totalSpend]);

  const formatCurrency = (v: number) => `€${v.toFixed(2)}`;
  const totalAB = variantCounts.A + variantCounts.B;
  if (totalAB === 0) return null;

  const pctA = variantCounts.A / totalAB;
  const pctB = variantCounts.B / totalAB;
  const currentSpend = spend ?? 0;
  const spendA = currentSpend * pctA;
  const spendB = currentSpend * pctB;
  const cplA = variantCompletions.A > 0 ? spendA / variantCompletions.A : 0;
  const cplB = variantCompletions.B > 0 ? spendB / variantCompletions.B : 0;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      <h3 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
        <Euro className="w-4 h-4" />
        A/B Test Spend Verdeling
        <span className="ml-auto flex items-center gap-1.5 text-xs font-medium text-emerald-600">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          LIVE
        </span>
      </h3>
      <p className="text-xs text-gray-400 mb-4">Proportioneel op basis van pageview-verdeling</p>

      {loadingSpend && spend === null ? (
        <div className="text-gray-400 text-sm animate-pulse py-4 text-center">Spend laden...</div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: 'Variant A', color: 'text-blue-600', pct: pctA, spend: spendA, pv: variantCounts.A, comp: variantCompletions.A, cpl: cplA, otherCpl: cplB },
            { label: 'Variant B', color: 'text-violet-600', pct: pctB, spend: spendB, pv: variantCounts.B, comp: variantCompletions.B, cpl: cplB, otherCpl: cplA },
          ].map(v => (
            <div key={v.label} className="bg-gray-50 rounded-xl p-3">
              <div className="flex items-center justify-between mb-2">
                <span className={`text-xs font-semibold ${v.color}`}>{v.label}</span>
                <span className="text-[10px] text-gray-400">{(v.pct * 100).toFixed(1)}% traffic</span>
              </div>
              <p className="text-lg font-bold text-gray-900">{formatCurrency(v.spend)}</p>
              <div className="mt-2 space-y-1 text-xs text-gray-500">
                <div className="flex justify-between">
                  <span>Pageviews</span>
                  <span className="font-medium text-gray-700">{v.pv}</span>
                </div>
                <div className="flex justify-between">
                  <span>Voltooiingen</span>
                  <span className="font-medium text-gray-700">{v.comp}</span>
                </div>
                <div className="flex justify-between">
                  <span>Kosten/voltooiing</span>
                  <span className={`font-medium ${v.cpl > 0 && v.otherCpl > 0 ? (v.cpl <= v.otherCpl ? 'text-emerald-600' : 'text-red-500') : 'text-gray-700'}`}>
                    {v.cpl > 0 ? formatCurrency(v.cpl) : '—'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ABSpendSplit;
